import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Clients from "./pages/Clients";
import AddClient from "./pages/AddClient";
import AddEmployee from "./pages/AddEmployee";
import Employees from "./pages/Employees";
import AddBroker from "./pages/AddBroker";
import Brokers from "./pages/Brokers";
import Deposit from "./pages/Deposit";
import Withdraw from "./pages/Withdraw";
import UserTransactions from "./pages/UserTransactions";
import LastSettled from "./pages/LastSettled";
import BalanceWithdrwal from "./pages/BalanceWithdrwal";

import ProtectedRoute from "./components/ProtectedRoute";
import AllUser from "./pages/AllUser";
import BrokerDeposit from "./pages/BrokerDeposit";
import BrokerWithdraw from "./pages/BrokerWithdraw";
import BrokerTransactions from "./pages/BrokerTransactions";
import BrokerPaymentStatus from "./pages/BrokerPaymentStatus";
import BrokerBalanceWithdrawal from "./pages/BrokerBalanceWithdrawal";
import AllBokers from "./pages/AllBokers";
import List from "./pages/List";
import UserDashboard from "./userPages/UserDashboard";
import CreateAccount from "./pages/CreateAccount";
import Reports from "./pages/Reports";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

const Layout = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check for token and role on component mount and when they change
  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");
    setIsAuthenticated(!!(token && role));
  }, []);

  // Update auth state when localStorage changes (useful for login/logout)
  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem("token");
      const role = localStorage.getItem("role");
      setIsAuthenticated(!!(token && role));
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  return (
    <Router>
      <div className="flex flex-col min-h-screen">
        {/* Only show Navbar if authenticated */}
        {isAuthenticated && <Navbar />}

        <main className={`${isAuthenticated ? "flex-grow" : "min-h-screen"}`}>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            {/* Redirect to login if not authenticated */}
            <Route
              path="/"
              element={
                isAuthenticated ? (
                  <Navigate to="/admin/dashboard" />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            {/* Protected routes - only accessible if authenticated */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute element={<Home />} allowedRoles={["admin"]} />
              }
            />
            <Route
              path="/admin/createadmin"
              element={
                <ProtectedRoute
                  element={<CreateAccount />}
                  allowedRoles={["admin"]}
                />
              }
            />

            {/* Role-based Access Examples */}
            <Route
              path="/add-client"
              element={
                <ProtectedRoute
                  element={<AddClient />}
                  allowedRoles={["admin"]}
                />
              }
            />
            <Route
              path="/clients"
              element={
                <ProtectedRoute
                  element={<Clients />}
                  allowedRoles={["admin"]}
                />
              }
            />
            <Route
              path="/add-employee"
              element={
                <ProtectedRoute
                  element={<AddEmployee />}
                  allowedRoles={["admin"]}
                />
              }
            />
            <Route
              path="/employees"
              element={
                <ProtectedRoute
                  element={<Employees />}
                  allowedRoles={["admin"]}
                />
              }
            />
            <Route
              path="/add-broker"
              element={
                <ProtectedRoute
                  element={<AddBroker />}
                  allowedRoles={["admin"]}
                />
              }
            />
            <Route
              path="/brokers"
              element={
                <ProtectedRoute
                  element={<Brokers />}
                  allowedRoles={["admin"]}
                />
              }
            />

            {/* Dynamic User Actions (Deposit, Withdraw, Transactions) */}
            <Route
              path="/client/deposit/:id/:userId"
              element={
                <ProtectedRoute
                  element={<Deposit />}
                  allowedRoles={["admin"]}
                />
              }
            />
            <Route
              path="/client/withdraw/:id/:userId"
              element={
                <ProtectedRoute
                  element={<Withdraw />}
                  allowedRoles={["admin"]}
                />
              }
            />
            <Route
              path="/client/transactions/:id/:userId"
              element={
                <ProtectedRoute
                  element={<UserTransactions />}
                  allowedRoles={["admin"]}
                />
              }
            />
            <Route
              path="/all-users"
              element={
                <ProtectedRoute
                  element={<AllUser />}
                  allowedRoles={["admin"]}
                />
              }
            />

            <Route
              path="/client/payment-status/:id/:userId"
              element={
                <ProtectedRoute
                  element={<LastSettled />}
                  allowedRoles={["admin"]}
                />
              }
            />
            <Route
              path="/client/balance-withdrawal/:id/:userId"
              element={
                <ProtectedRoute
                  element={<BalanceWithdrwal />}
                  allowedRoles={["admin"]}
                />
              }
            />

            <Route
              path="/broker/deposit/:id/:userId"
              element={
                <ProtectedRoute
                  element={<BrokerDeposit />}
                  allowedRoles={["admin"]}
                />
              }
            />
            <Route
              path="/broker/withdraw/:id/:userId"
              element={
                <ProtectedRoute
                  element={<BrokerWithdraw />}
                  allowedRoles={["admin"]}
                />
              }
            />
            <Route
              path="/broker/transactions/:id/:userId"
              element={
                <ProtectedRoute
                  element={<BrokerTransactions />}
                  allowedRoles={["admin"]}
                />
              }
            />

            <Route
              path="/broker/payment-status/:id/:userId"
              element={
                <ProtectedRoute
                  element={<BrokerPaymentStatus />}
                  allowedRoles={["admin"]}
                />
              }
            />
            <Route
              path="/broker/balance-withdrawal/:id/:userId"
              element={
                <ProtectedRoute
                  element={<BrokerBalanceWithdrawal />}
                  allowedRoles={["admin"]}
                />
              }
            />

            <Route
              path="/all-brokers"
              element={
                <ProtectedRoute
                  element={<AllBokers />}
                  allowedRoles={["admin"]}
                />
              }
            />
            <Route
              path="/list"
              element={
                <ProtectedRoute element={<List />} allowedRoles={["admin"]} />
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute
                  element={<Reports />}
                  allowedRoles={["admin"]}
                />
              }
            />
            {/* user routes */}
            <Route
              path="/user/dashboard"
              element={
                <ProtectedRoute
                  element={<UserDashboard />}
                  allowedRoles={["user"]}
                />
              }
            />
            <Route
              path="/user/transactions/:id"
              element={
                <ProtectedRoute
                  element={<UserTransactions />}
                  allowedRoles={["user"]}
                />
              }
            />
            {/* Catch all other routes and redirect to login if not authenticated */}
            <Route
              path="*"
              element={
                isAuthenticated ? (
                  <Navigate to="/admin/dashboard" />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
          </Routes>
        </main>

        {/* Only show Footer if authenticated */}
        {isAuthenticated && <Footer />}
      </div>
    </Router>
  );
};

export default Layout;
